import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import adminApi, { type LLMUsageResponse } from '@/services/admin.api';
import BillingTimeframeSelect, {
  timeframeParams,
  useBillingTimeframe,
} from '@/components/billing/BillingTimeframeSelect';
import { formatTokensCompact, formatTokensExact } from '@/utils/formatTokens.util';

type LLMUsage = LLMUsageResponse;

const BillingAI: React.FC = () => {
  const [usage, setUsage] = useState<LLMUsage>({
    tokensUsed: 0,
    totalCost: 0,
    requestCount: 0,
    aiMessageCount: 0,
    avgTokensPerRequest: 0,
    modelBreakdown: [],
  });
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useBillingTimeframe();

  useEffect(() => {
    let cancelled = false;
    const fetchUsage = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await adminApi.getLLMUsage(timeframeParams(timeframe));
        if (!cancelled) setUsage(response);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load AI billing');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoaded(true);
        }
      }
    };

    fetchUsage();
    return () => {
      cancelled = true;
    };
  }, [timeframe]);

  if (loading && !loaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" mb={1}>
            Billing AI
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI-focused billing details with model usage share and estimated spend.
          </Typography>
        </Box>
        <BillingTimeframeSelect
          value={timeframe}
          onChange={setTimeframe}
          currentPeriod={usage.currentPeriod}
          loadPeriods={async () => (await adminApi.getUsagePeriods({ page: 1, limit: 12 })).data}
        />
      </Stack>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Billing AI Amount
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                ${usage.totalCost.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Token Spend
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {formatTokensCompact(usage.tokensUsed)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTokensExact(usage.tokensUsed)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                AI Requests
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {usage.requestCount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Tokens / Request
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {formatTokensCompact(usage.avgTokensPerRequest)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Model Usage Share
              </Typography>
              {usage.modelBreakdown.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No AI usage available yet.
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {usage.modelBreakdown.map((item) => {
                    const share = usage.tokensUsed > 0 ? (item.tokens / usage.tokensUsed) * 100 : 0;
                    return (
                      <Box key={item.model}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {item.model}
                          </Typography>
                          <Tooltip title={formatTokensExact(item.tokens)}>
                            <Typography variant="body2" color="text.secondary">
                              {formatTokensCompact(item.tokens)} tokens | ${item.cost.toFixed(2)}
                            </Typography>
                          </Tooltip>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(share, 100)}
                          sx={{ height: 8, borderRadius: 999 }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BillingAI;
